// Custom implementation of https://github.com/mustaphaturhan/chakra-ui-markdown-renderer
import React /*, { isValidElement }*/ from 'react';
import deepmerge from 'deepmerge';
import { Components } from 'react-markdown';
import {
  chakra,
  // Checkbox,
  Code,
  Container,
  Separator,
  Heading,
  Image,
  Link,
  List,
  Table,
  Text
} from '@chakra-ui/react';
// import CtaBanner from '../CtaBanner';

type GetCoreProps = {
  children?: React.ReactNode;
  'data-sourcepos'?: any;
};

function getCoreProps(props: GetCoreProps): any {
  return props['data-sourcepos'] ? { 'data-sourcepos': props['data-sourcepos'] } : {};
}

interface Defaults extends Components {
  /**
   * @deprecated Use `h1, h2, h3, h4, h5, h6` instead.
   */
  heading?: Components['h1'];
}

export const defaults: Defaults = {
  p: ({ children }) => (
    <Text mb={4} fontSize='lg'>
      {children}
    </Text>
  ),
  em: (props) => {
    const { children } = props;
    return <Text as='em'>{children}</Text>;
  },
  blockquote: (props) => {
    const { children } = props;

    // Support CTA sections if children.props.children contains the string CTA:
    // const cta =
    //   isValidElement(children[1]) &&
    //   typeof children[1].props?.children?.[0] === 'string' &&
    //   children[1].props.children[0].includes('CTA:');
    // if (cta) {
    //   // @ts-ignore
    //   const [, title, subTitle] = children[1].props.children[0].split(':').map((s) => s.trim());
    //   return <CtaBanner title={title} subTitle={subTitle} />;
    // }

    return (
      <Code as='blockquote' p={2}>
        {children}
      </Code>
    );
  },
  code: (props) => {
    const { /* inline, */ children, className } = props;

    // if (inline) {
    //   return <Code p={2}>{children}</Code>;
    // }

    return (
      <Code className={className} whiteSpace='break-spaces' display='block' w='full' p={2}>
        {children}
      </Code>
    );
  },
  del: (props) => {
    const { children } = props;
    return <Text as='del'>{children}</Text>;
  },
  hr: () => <Separator />,
  // @ts-ignore
  a: Link,
  // @ts-ignore
  img: Image,
  text: (props) => {
    const { children } = props;
    return <Text as='span'>{children}</Text>;
  },
  ul: (props) => {
    const { children, node } = props;
    const attrs = getCoreProps(props);
    let styleType = 'disc';
    if (node.tagName === 'ol') {
      styleType = 'decimal';
    }
    // if (depth === 1) styleType = 'circle';
    return (
      <List.Root
        spacing={2}
        as={node.tagName === 'ol' ? 'ol' : 'ul'}
        listStyleType={styleType}
        pl={4}
        mb={6}
        {...attrs}
        // Override font size to be the same for all sub-items
        fontSize='lg'
      >
        {children}
      </List.Root>
    );
  },
  ol: (props) => {
    const { node, children } = props;
    const attrs = getCoreProps(props);

    let styleType = 'disc';
    if (node.tagName === 'ol') {
      styleType = 'decimal';
    }
    return (
      <List.Root
        gap={2}
        as={node.tagName === 'ol' ? 'ol' : 'ul'}
        listStyleType={styleType}
        pl={4}
        mb={6}
        css={{
          counterReset: 'item',
          '& > li': {
            display: 'ruby',
            counterIncrement: 'item',
            '&::before': {
              // Top
              color: 'fg.subtle',
              content: 'counters(item, ".") ".\\00a0"',
              marginRight: '0.5em'
            }
          },
          '& li': {
            listStyleType: 'none'
          },
          '& ol': {
            counterReset: 'item',
            listStyle: 'none',
            pl: 4,
            mt: 2,
            mb: 2
          },
          '& ol > li': {
            // Flex for inline but doesn't work on nested of nested items.
            display: 'block',
            pl: 1,
            mt: 1
          },
          '& ol > li::before': {
            content: 'counters(item, ".") ".\\00a0"',
            // Nested 1.1
            color: 'fg.subtle',
            marginRight: '0.5rem'
          }
        }}
        {...attrs}
        // Override font size to be the same for all sub-items
        fontSize='lg'
      >
        {children}
      </List.Root>
    );
  },
  li: (props) => {
    const { children } = props;
    let checkbox = null;
    // TODO: fix checkbox markup lookup
    // if (checked !== null && checked !== undefined) {
    //   checkbox = (
    //     <Checkbox.Root defaultChecked={checked} readOnly>
    //       <Checkbox.Control />
    //       <Checkbox.Label>{children}</Checkbox.Label>
    //     </Checkbox.Root>
    //   );
    // }
    return (
      <List.Item
        {...getCoreProps(props)}
        // listStyleType={checked !== null ? 'none' : 'inherit'}
      >
        {checkbox || children}
      </List.Item>
    );
  },
  h1: ({ children }) => (
    <Heading as='h1' mt={10} mb={2} size='md' fontSize='3xl' fontWeight='extrabold'>
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading as='h2' mt={10} mb={2} size='md' fontSize='2xl' fontWeight='extrabold'>
      {children}
    </Heading>
  ),

  h3: ({ children }) => (
    <Heading as='h3' mt={8} mb={2} size='sm' fontSize='xl' fontWeight='extrabold'>
      {children}
    </Heading>
  ),
  h4: ({ children }) => (
    <Heading as='h4' mt={6} mb={2} size='xs' fontSize='xl' fontWeight='bold'>
      {children}
    </Heading>
  ),
  /**
   * @deprecated Use `h1, h2, h3, h4, h5, h6` instead.
   */
  heading: (props) => {
    const { /* level, */ children } = props;
    const level = 1;
    const sizes = ['2xl', 'xl', 'lg', 'md', 'sm', 'xs'];
    return (
      <Heading my={4} as={`h${level}`} size={sizes[`${level - 1}`]} {...getCoreProps(props)}>
        {children}
      </Heading>
    );
  },
  pre: (props) => {
    const { children } = props;
    return <chakra.pre {...getCoreProps(props)}>{children}</chakra.pre>;
  },
  // table: Table,
  table: ({ children }) => (
    <Container mb={6} border='1px' borderColor='bg-default' bg='bg-surface' borderRadius='2xl' boxShadow='sm'>
      <Table.Root variant='line'>{children}</Table.Root>
    </Container>
  ),
  thead: ({ children }) => <Table.Header>{children}</Table.Header>,
  tbody: ({ children }) => <Table.Body>{children}</Table.Body>,
  tr: (props) => <Table.Row>{props.children}</Table.Row>,
  td: (props) => <Table.Cell>{props.children}</Table.Cell>,
  th: (props) => <Table.ColumnHeader>{props.children}</Table.ColumnHeader>
};

function ChakraUIRenderer(theme?: Defaults, merge = true): Components {
  const elements: Components = {
    p: defaults.p,
    em: defaults.em,
    blockquote: defaults.blockquote,
    code: defaults.code,
    del: defaults.del,
    hr: defaults.hr,
    a: defaults.a,
    img: defaults.img,
    text: defaults.text,
    ul: defaults.ul,
    ol: defaults.ol,
    li: defaults.li,
    h1: defaults.h1,
    h2: defaults.h2,
    h3: defaults.h3,
    h4: defaults.h4,
    h5: defaults.heading,
    h6: defaults.heading,
    pre: defaults.pre,
    table: defaults.table,
    thead: defaults.thead,
    tbody: defaults.tbody,
    tr: defaults.tr,
    td: defaults.td,
    th: defaults.th
  };

  if (theme && merge) {
    return deepmerge(elements, theme) as Components;
  }

  return elements;
}

export default ChakraUIRenderer;
